import React, { useState, useEffect } from 'react';
import {
  Form,
  Input,
  Button,
  Select,
  DatePicker,
  TimePicker,
  InputNumber,
  message,
  Divider,
  Row,
  Col,
  Alert,
  Modal,
  Typography,
  Checkbox,
  Tag
} from 'antd';
import { FileAddOutlined, QuestionCircleOutlined, CalculatorOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import useDocumentGeneration from '../../hooks/useDocumentGeneration';
import { supabase } from '../../api/supabase';
import { colors, spacing, radius, typography } from '../../styles/monochromeTheme';

const { Text } = Typography;
const { Option } = Select;

const CHARTER_TYPES = [
  { value: 'sbh_sxm', label: 'SBH → SXM', from: null, to: null, cruising: 'Transfer from Saint Barth to St Martin' },
  { value: 'sxm_sbh', label: 'SXM → SBH', from: null, to: null, cruising: 'Transfer from St Martin to Saint Barth' },
  { value: 'sbh_axa', label: 'SBH → AXA', from: null, to: null, cruising: 'Transfer from Saint Barth to Anguilla' },
  { value: 'axa_sbh', label: 'AXA → SBH', from: null, to: null, cruising: 'Transfer from Anguilla to Saint Barth' },
  { value: 'sxm_axa', label: 'SXM → AXA', from: null, to: null, cruising: 'Transfer from St Martin to Anguilla' },
  { value: 'axa_sxm', label: 'AXA → SXM', from: null, to: null, cruising: 'Transfer from Anguilla to St Martin' },
  { value: 'full_day_sbh', label: 'Full Day SBH', from: '09:00', to: '17:00', cruising: 'Full day around Saint Barth' },
  { value: 'full_day_sxm', label: 'Full Day SXM', from: '09:00', to: '17:00', cruising: 'Full day around St Martin' },
  { value: 'full_day_axa', label: 'Full Day AXA', from: '09:00', to: '17:00', cruising: 'Full day around Anguilla' },
  { value: 'half_day_sbh_am', label: 'Half Day SBH Matin', from: '09:00', to: '13:00', cruising: 'Half day 9h-13h around Saint Barth' },
  { value: 'half_day_sbh_pm', label: 'Half Day SBH Après-midi', from: '13:00', to: '17:00', cruising: 'Half day 13h-17h around Saint Barth' },
  { value: 'half_day_sbh_pm_sunset', label: 'Half Day Après-midi + Sunset', from: '13:00', to: '19:00', cruising: 'Half day 13h-17h + Sunset 17h-19h around Saint Barth' },
  { value: 'sunset', label: 'Sunset', from: '17:00', to: '19:00', cruising: 'Sunset 17h-19h around Saint Barth' },
  { value: 'custom', label: 'Personnalisé', from: null, to: null, cruising: '' },
];

// Monochrome styles
const styles = {
  page: {
    minHeight: '100vh',
    background: colors.bgPrimary,
    padding: spacing.xxl,
  },
  card: {
    background: colors.bgSecondary,
    borderRadius: radius.xxxl,
    border: `1px solid ${colors.borderLight}`,
    padding: spacing.xxl,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: spacing.xl,
    background: colors.bgSecondary,
    borderRadius: radius.xxxl,
    border: `1px solid ${colors.borderLight}`,
    padding: `${spacing.lg} ${spacing.xl}`,
  },
  headerIcon: {
    width: '48px',
    height: '48px',
    borderRadius: radius.xl,
    background: colors.bgDark,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '24px',
  },
  autoBtn: {
    fontSize: '11px',
    height: '32px',
    borderRadius: radius.md,
    background: colors.bgDark,
    color: colors.textInverse,
    border: 'none',
    fontWeight: typography.fontWeight.medium,
  },
  primaryBtn: {
    height: '52px',
    borderRadius: radius.xl,
    background: colors.bgDark,
    border: 'none',
    color: colors.textInverse,
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.bold,
  },
  tag: {
    background: colors.bgTertiary,
    color: colors.textSecondary,
    border: `1px solid ${colors.borderLight}`,
    fontSize: '12px',
    padding: '4px 12px',
    borderRadius: radius.md,
  },
};

function CharterDocumentGenerator() {
  const [form] = Form.useForm();
  const [hasError, setHasError] = useState(false);
  const [isTransfer, setIsTransfer] = useState(false);
  const [showTransportTaxes, setShowTransportTaxes] = useState(true); // Show transport/taxes fields
  const [showDiscount, setShowDiscount] = useState(false);
  const [showExtra, setShowExtra] = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const [discountPercent, setDiscountPercent] = useState(0);
  const [debugData, setDebugData] = useState(null);
  const [debugVisible, setDebugVisible] = useState(false);
  const [isDirectCharterer, setIsDirectCharterer] = useState(false);
  const [yachts, setYachts] = useState([]);
  const navigate = useNavigate();

  const { generateDoc, loading } = useDocumentGeneration();

  useEffect(() => {
    const fetchYachts = async () => {
      try {
        const { data, error } = await supabase
          .from('yachts')
          .select('id, name, brand, length_feet')
          .order('length_feet', { ascending: true });

        if (error) throw error;

        if (data && data.length > 0) {
          const formattedYachts = data.map(yacht => ({
            key: yacht.id,
            brand: yacht.brand || '',
            name: yacht.name || '',
            length: yacht.length_feet?.toString() || ''
          }));
          setYachts(formattedYachts);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des yachts:', error);
      }
    };

    fetchYachts();
  }, []);

  const calculateTotal = () => {
    const boatPrice = parseFloat(form.getFieldValue('boat_price')) || 0;
    const transportPrice = parseFloat(form.getFieldValue('transport_price')) || 0;
    const taxesPrice = parseFloat(form.getFieldValue('taxes_price')) || 0;
    const discountPrice = parseFloat(form.getFieldValue('discount_price')) || 0;
    const extraHourPrice = parseFloat(form.getFieldValue('extra_hour_price')) || 0;
    const customPrice = parseFloat(form.getFieldValue('custom_price')) || 0;
    const total = boatPrice + transportPrice + taxesPrice - discountPrice + extraHourPrice + customPrice;
    form.setFieldsValue({ total_price: total });
  };

  // Calculate discount from percentage (based on boat price only)
  const handleDiscountPercentChange = (percent) => {
    setDiscountPercent(percent || 0);
    const boatPrice = parseFloat(form.getFieldValue('boat_price')) || 0;
    const discountAmount = Math.round(boatPrice * (percent || 0) / 100);
    form.setFieldsValue({ discount_price: discountAmount, discount_text: `${percent}%` });
    calculateTotal();
  };

  const updatePriceAndCruisingArea = () => {
    const yachtKey = form.getFieldValue('yacht');
    const charterType = form.getFieldValue('charter_type');
    if (!yachtKey || !charterType) return;
    const yacht = yachts.find(y => y.key === yachtKey);
    if (!yacht || !yacht.fullData) return;

    const yachtData = yacht.fullData;
    let price = 0;
    let cruisingArea = '';

    switch(charterType) {
      case 'sbh_sxm': price = parseFloat(yachtData.day_sbh_sxm_price || yachtData.transfer_st_barth_to_st_martin || 0); cruisingArea = 'SBH/SXM Transfer'; break;
      case 'sxm_sbh': price = parseFloat(yachtData.day_sxm_sbh_price || yachtData.transfer_st_martin_to_st_barth || 0); cruisingArea = 'SXM/SBH Transfer'; break;
      case 'sbh_axa': price = parseFloat(yachtData.transfer_st_barth_to_anguilla || yachtData.transfer_anguilla_to_st_barth || 0); cruisingArea = 'SBH/AXA Transfer'; break;
      case 'axa_sbh': price = parseFloat(yachtData.transfer_anguilla_to_st_barth || 0); cruisingArea = 'AXA/SBH Transfer'; break;
      case 'sxm_axa': price = parseFloat(yachtData.transfer_st_martin_to_anguilla || yachtData.transfer_anguilla_to_st_martin || 0); cruisingArea = 'SXM/AXA Transfer'; break;
      case 'axa_sxm': price = parseFloat(yachtData.transfer_anguilla_to_st_martin || 0); cruisingArea = 'AXA/SXM Transfer'; break;
      case 'full_day_sbh': price = parseFloat(yachtData.full_day_st_barth || 0); cruisingArea = 'Full Day 9h-17h Around St Barthélémy'; break;
      case 'full_day_sxm': price = parseFloat(yachtData.full_day_st_martin || 0); cruisingArea = 'Full Day 9h-17h Around St Martin'; break;
      case 'full_day_axa': price = parseFloat(yachtData.full_day_anguilla || 0); cruisingArea = 'Full Day 9h-17h Around Anguilla'; break;
      case 'half_day_sbh_am': price = parseFloat(yachtData.half_day_st_barth_morning || 0); cruisingArea = 'Half Day 9h-13h Around St Barthélémy'; break;
      case 'half_day_sbh_pm': price = parseFloat(yachtData.half_day_st_barth_afternoon || 0); cruisingArea = 'Half Day 13h-17h Around St Barthélémy'; break;
      case 'half_day_sbh_pm_sunset':
        price = parseFloat(yachtData.half_day_st_barth_afternoon || 0) + parseFloat(yachtData.sunset_price || 0);
        cruisingArea = 'Half Day 13h-17h + Sunset 17h-19h Around St Barthélémy';
        break;
      case 'sunset': price = parseFloat(yachtData.sunset_price || 0); cruisingArea = 'Sunset 17h-19h Around St Barthélémy'; break;
      default: cruisingArea = ''; break;
    }

    if (price > 0) { form.setFieldsValue({ boat_price: price }); calculateTotal(); }
    if (cruisingArea) { form.setFieldsValue({ cruising_area: cruisingArea }); }
  };

  const handleYachtChange = (value) => {
    const yacht = yachts.find(y => y.key === value);
    if (yacht) {
      form.setFieldsValue({ name_of_vessel: yacht.name || '' });
      updatePriceAndCruisingArea();
    }
  };

  const handleCharterTypeChange = (value) => {
    const charter = CHARTER_TYPES.find(c => c.value === value);
    const isTransferType = value && !value.startsWith('full_day_') && !value.startsWith('half_day_') && value !== 'sunset' && value !== 'custom';
    setIsTransfer(isTransferType);

    // Show transport/taxes only for transfers (not for full day, half day, sunset)
    const needsTransportTaxes = isTransferType;
    setShowTransportTaxes(needsTransportTaxes);

    if (charter) {
      const updates = { cruising_area: charter.cruising || '' };
      if (charter.from) {
        updates.from_hour = dayjs(charter.from, 'HH:mm');
      } else {
        updates.from_hour = null;
      }
      if (charter.to) {
        updates.to_hour = dayjs(charter.to, 'HH:mm');
      } else {
        updates.to_hour = null;
      }
      form.setFieldsValue(updates);
    }

    // Reset transport/taxes if not needed
    if (!needsTransportTaxes) {
      form.setFieldsValue({
        transport_price: 0,
        transport_text: '',
        flight_info: ''
      });
    }

    updatePriceAndCruisingArea();
    calculateTotal();
  };

  const handleGenerate = async (values) => {
    setHasError(false);
    try {
      const today = dayjs().format('DD/MM/YYYY');
      const charterDate = values.charter_date ? values.charter_date.format('DD/MM/YYYY') : '';
      const fromHour = values.from_hour ? values.from_hour.format('HH:mm') : '';
      const toHour = values.to_hour ? values.to_hour.format('HH:mm') : '';
      const yacht = yachts.find(y => y.key === values.yacht);

      // Get cruising_area directly from form in case values doesn't have it
      const cruisingArea = values.cruising_area || form.getFieldValue('cruising_area') || '';

      console.log('=== YACHT CONTRACT DATA ===');
      console.log('cruising_area from values:', values.cruising_area);
      console.log('cruising_area from form:', form.getFieldValue('cruising_area'));
      console.log('cruising_area final:', cruisingArea);

      const documentData = {
        today_date: today, generation_date: today, charter_date: charterDate, from_hour: fromHour, to_hour: toHour,
        client_name: values.name, charterer: values.charterer || '', brand_of_vessel: yacht?.brand || '',
        name_of_vessel: values.name_of_vessel || '', length: yacht?.length || '', cruising_area: cruisingArea,
        flight_info: values.flight_info || '', pax_number: values.guests_number || '',
        boat_price: values.boat_price ? `${values.boat_price}€ (BOAT PRICE)` : '',
        transport_price: values.transport_price ? `+ ${values.transport_price}€` : '', transport_text: values.transport_text || '',
        taxes_price: values.taxes_price ? `+ ${values.taxes_price}€` : '', taxes_text: values.taxes_text || '',
        discount_price: values.discount_price ? `- ${values.discount_price}€` : '', discount_text: values.discount_text || '',
        extra_hour_price: values.extra_hour_price ? `+ ${values.extra_hour_price}€` : '', extra_hour_text: values.extra_hour_text || '',
        custom_price: values.custom_price ? `+ ${values.custom_price}€` : '', custom_text: values.custom_text || '',
        total_price: values.total_price ? `${values.total_price} EUR` : '',
      };

      console.log('Full documentData:', documentData);

      setDebugData(documentData);
      const charterId = `charter_${Date.now()}`;

      try {
        await generateDoc('Contrat', documentData, charterId, 'bateaux');
        form.resetFields();
        setDebugVisible(true);
      } catch (error) {
        if (error.message.includes('PROBLÈME DE FORMATAGE') || error.message.includes('Duplicate')) { setHasError(true); }
        message.error(`Erreur Contrat: ${error.message}`, 10);
      }
    } catch (error) {
      message.error('Erreur générale: ' + error.message);
    }
  };

  return (
    <div style={styles.page}>
      <style>
        {`
          .ant-picker-input > input, .ant-picker-input > input::placeholder { color: ${colors.textPrimary} !important; }
          .ant-form-item-label > label { font-size: 11px !important; font-weight: 600 !important; color: ${colors.textSecondary} !important; text-transform: uppercase; letter-spacing: 0.5px; }
          .ant-input, .ant-select-selector, .ant-picker, .ant-input-number { border-radius: ${radius.md} !important; border-color: ${colors.borderMedium} !important; }
          .ant-input:focus, .ant-select-selector:focus, .ant-picker-focused { border-color: ${colors.bgDark} !important; box-shadow: 0 0 0 2px rgba(0,0,0,0.1) !important; }
          .ant-divider-inner-text { font-size: 11px !important; font-weight: 600 !important; color: ${colors.textMuted} !important; text-transform: uppercase; letter-spacing: 1px; }
        `}
      </style>

      {/* Header */}
      <div style={styles.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: spacing.lg }}>
          <div style={styles.headerIcon}>🚤</div>
          <div>
            <h2 style={{ margin: 0, fontSize: typography.fontSize.xl, fontWeight: typography.fontWeight.bold, color: colors.textPrimary, letterSpacing: typography.letterSpacing.tight }}>
              Yacht Charter Contract
            </h2>
            <p style={{ margin: 0, fontSize: typography.fontSize.sm, color: colors.textTertiary, marginTop: spacing.xs }}>
              Generate contract - Confirmation & Invoice from document list
            </p>
          </div>
        </div>
        <Tag style={styles.tag}>Google Drive API</Tag>
      </div>

      <div style={styles.card}>
        {hasError && (
          <Alert
            message="Template Formatting Error Detected"
            description={
              <div>
                <p style={{ fontSize: '11px', marginBottom: '8px' }}>Variables in your Word templates are broken by formatting.</p>
                <Button type="link" icon={<QuestionCircleOutlined />} onClick={() => navigate('/template-guide')} style={{ paddingLeft: 0, fontSize: '11px', height: 'auto' }}>
                  View Template Correction Guide
                </Button>
              </div>
            }
            type="error" closable onClose={() => setHasError(false)} style={{ marginBottom: '16px', borderRadius: radius.lg }}
          />
        )}

        <Form
          form={form} layout="vertical" onFinish={handleGenerate} onValuesChange={calculateTotal}
          initialValues={{ boat_price: 0, transport_price: 0, transport_text: '', taxes_price: 0, taxes_text: '', discount_price: 0, discount_text: '', extra_hour_price: 0, extra_hour_text: '', custom_price: 0, custom_text: '', total_price: 0 }}
        >
          <Row gutter={[12, 0]}>
            <Col span={8}>
              <Form.Item name="name" label="Client Name" rules={[{ required: true, message: 'Required' }]} style={{ marginBottom: 12 }}>
                <Input placeholder="John Doe" style={{ height: '40px' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="charterer" label="Hotel / Agency" style={{ marginBottom: 4 }}>
                <Input placeholder="Hotel Christopher" disabled={isDirectCharterer} style={{ height: '40px' }} />
              </Form.Item>
              <Checkbox checked={isDirectCharterer} onChange={(e) => { setIsDirectCharterer(e.target.checked); form.setFieldsValue({ charterer: e.target.checked ? 'direct' : '' }); }} style={{ fontSize: '11px' }}>
                Direct
              </Checkbox>
            </Col>
            <Col span={4}>
              <Form.Item name="charter_date" label="Date" rules={[{ required: true, message: 'Req' }]} style={{ marginBottom: 12 }}>
                <DatePicker format="DD/MM/YYYY" style={{ width: '100%', height: '40px' }} />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item name="guests_number" label="Pax" rules={[{ required: true, message: 'Req' }]} style={{ marginBottom: 12 }}>
                <InputNumber min={1} max={20} style={{ width: '100%', height: '40px' }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[12, 0]}>
            <Col span={6}>
              <Form.Item name="charter_type" label="Charter Type" rules={[{ required: true, message: 'Req' }]} style={{ marginBottom: 12 }}>
                <Select placeholder="Select" onChange={handleCharterTypeChange} showSearch={false}>
                  {CHARTER_TYPES.map(type => <Option key={type.value} value={type.value}>{type.label}</Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col span={3}>
              <Form.Item name="from_hour" label="From" rules={[{ required: true, message: 'Req' }]} style={{ marginBottom: 12 }}>
                <TimePicker format="HH:mm" minuteStep={10} style={{ width: '100%', height: '40px' }} onChange={(time) => {
                  if (time && isTransfer) {
                    form.setFieldsValue({ to_hour: time.add(1, 'hour').add(15, 'minute') });
                  }
                }} />
              </Form.Item>
            </Col>
            <Col span={3}>
              <Form.Item name="to_hour" label="To" rules={[{ required: true, message: 'Req' }]} style={{ marginBottom: 12 }}>
                <TimePicker format="HH:mm" minuteStep={10} style={{ width: '100%', height: '40px' }} />
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item name="yacht" label="Yacht" rules={[{ required: true, message: 'Req' }]} style={{ marginBottom: 12 }}>
                <Select placeholder="Select" onChange={handleYachtChange} showSearch filterOption={(input, option) => `${option.children}`.toLowerCase().includes(input.toLowerCase())}>
                  {yachts.map(yacht => <Option key={yacht.key} value={yacht.key}>{yacht.brand} {yacht.length}ft</Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item name="name_of_vessel" label="Name" style={{ marginBottom: 12 }}>
                <Input readOnly style={{ height: '40px', backgroundColor: colors.bgTertiary }} />
              </Form.Item>
            </Col>
            <Col span={3}>
              <Form.Item name="cruising_area" label="Zone" style={{ marginBottom: 12 }}>
                <Input placeholder="Auto" style={{ height: '40px' }} />
              </Form.Item>
            </Col>
            {isTransfer && (
              <Col span={24}>
                <Form.Item name="flight_info" label="Flight Info" style={{ marginBottom: 12 }}>
                  <Input placeholder="AF123 / AA456" style={{ height: '40px' }} />
                </Form.Item>
              </Col>
            )}
          </Row>

          <Divider style={{ margin: '16px 0' }}>
            <CalculatorOutlined style={{ marginRight: '8px' }} /> Pricing
          </Divider>

          <Row gutter={[8, 0]}>
            <Col span={4}>
              <Form.Item name="boat_price" label="Boat" rules={[{ required: true, message: 'Req' }]} style={{ marginBottom: 12 }}>
                <InputNumber min={0} style={{ width: '100%', height: '40px' }} onChange={calculateTotal} addonAfter="€" />
              </Form.Item>
            </Col>

            {/* Transport - Only show for transfers */}
            {showTransportTaxes && (
              <>
                <Col span={3}>
                  <Form.Item name="transport_text" label="Transport" style={{ marginBottom: 12 }}>
                    <Select
                      placeholder="Select"
                      style={{ width: '100%' }}
                      onChange={(value) => {
                        form.setFieldsValue({ transport_text: value });
                      }}
                    >
                      <Option value="VIP">VIP</Option>
                      <Option value="Standard">Standard</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={2}>
                  <Form.Item name="transport_price" label="Price" style={{ marginBottom: 12 }}>
                    <InputNumber min={0} style={{ width: '100%', height: '40px' }} placeholder="0" onChange={calculateTotal} />
                  </Form.Item>
                </Col>
              </>
            )}

            {/* Taxes - Only show for transfers */}
            {showTransportTaxes && (
              <>
                <Col span={2}>
                  <Form.Item name="taxes_price" label="Taxes" style={{ marginBottom: 12 }}>
                    <InputNumber min={0} style={{ width: '100%', height: '40px' }} placeholder="0" onChange={calculateTotal} />
                  </Form.Item>
                </Col>
                <Col span={3}>
                  <Form.Item name="taxes_text" label=" " style={{ marginBottom: 12 }}>
                    <Input placeholder="SXM" style={{ height: '40px' }} readOnly />
                  </Form.Item>
                </Col>
              </>
            )}

            <Col span={6}>
              <Form.Item name="total_price" label="TOTAL" style={{ marginBottom: 12 }}>
                <InputNumber min={0} style={{ width: '100%', height: '44px', fontWeight: 700, fontSize: '16px' }} addonAfter="€" />
              </Form.Item>
            </Col>
          </Row>

          {/* Optional fields toggles */}
          <Row gutter={[16, 0]} style={{ marginBottom: spacing.md }}>
            <Col>
              <Checkbox
                checked={showDiscount}
                onChange={(e) => {
                  setShowDiscount(e.target.checked);
                  if (!e.target.checked) {
                    form.setFieldsValue({ discount_price: 0, discount_text: '' });
                    setDiscountPercent(0);
                    calculateTotal();
                  }
                }}
              >
                <span style={{ fontWeight: 500 }}>+ Discount</span>
              </Checkbox>
            </Col>
            <Col>
              <Checkbox
                checked={showExtra}
                onChange={(e) => {
                  setShowExtra(e.target.checked);
                  if (!e.target.checked) {
                    form.setFieldsValue({ extra_hour_price: 0, extra_hour_text: '' });
                    calculateTotal();
                  }
                }}
              >
                <span style={{ fontWeight: 500 }}>+ Extra</span>
              </Checkbox>
            </Col>
            <Col>
              <Checkbox
                checked={showCustom}
                onChange={(e) => {
                  setShowCustom(e.target.checked);
                  if (!e.target.checked) {
                    form.setFieldsValue({ custom_price: 0, custom_text: '' });
                    calculateTotal();
                  }
                }}
              >
                <span style={{ fontWeight: 500 }}>+ Custom</span>
              </Checkbox>
            </Col>
          </Row>

          {/* Conditional optional fields */}
          {(showDiscount || showExtra || showCustom) && (
            <Row gutter={[8, 0]}>
              {showDiscount && (
                <>
                  <Col span={2}>
                    <Form.Item label="Discount %" style={{ marginBottom: 12 }}>
                      <InputNumber
                        min={0}
                        max={100}
                        value={discountPercent}
                        style={{ width: '100%', height: '40px' }}
                        placeholder="%"
                        onChange={handleDiscountPercentChange}
                        addonAfter="%"
                      />
                    </Form.Item>
                  </Col>
                  <Col span={2}>
                    <Form.Item name="discount_price" label="Amount" style={{ marginBottom: 12 }}>
                      <InputNumber min={0} style={{ width: '100%', height: '40px' }} placeholder="0" onChange={calculateTotal} />
                    </Form.Item>
                  </Col>
                  <Col span={2}>
                    <Form.Item name="discount_text" label="Text" style={{ marginBottom: 12 }}>
                      <Input placeholder="%" style={{ height: '40px' }} />
                    </Form.Item>
                  </Col>
                </>
              )}
              {showExtra && (
                <>
                  <Col span={2}>
                    <Form.Item name="extra_hour_price" label="Extra" style={{ marginBottom: 12 }}>
                      <InputNumber min={0} style={{ width: '100%', height: '40px' }} placeholder="0" onChange={calculateTotal} />
                    </Form.Item>
                  </Col>
                  <Col span={3}>
                    <Form.Item name="extra_hour_text" label=" " style={{ marginBottom: 12 }}>
                      <Input placeholder="Text" style={{ height: '40px' }} />
                    </Form.Item>
                  </Col>
                </>
              )}
              {showCustom && (
                <>
                  <Col span={2}>
                    <Form.Item name="custom_price" label="Custom" style={{ marginBottom: 12 }}>
                      <InputNumber min={0} style={{ width: '100%', height: '40px' }} placeholder="0" onChange={calculateTotal} />
                    </Form.Item>
                  </Col>
                  <Col span={3}>
                    <Form.Item name="custom_text" label=" " style={{ marginBottom: 12 }}>
                      <Input placeholder="Description" style={{ height: '40px' }} />
                    </Form.Item>
                  </Col>
                </>
              )}
            </Row>
          )}

          <Form.Item style={{ marginTop: spacing.xl, marginBottom: 0 }}>
            <Button type="primary" htmlType="submit" loading={loading} icon={<FileAddOutlined />} block style={styles.primaryBtn}>
              Generate Contract
            </Button>
          </Form.Item>
        </Form>
      </div>

      <Modal
        title={<span style={{ fontSize: '14px', fontWeight: 600 }}>Debug: Data sent to template</span>}
        open={debugVisible} onCancel={() => setDebugVisible(false)} width={800}
        footer={[<Button key="close" onClick={() => setDebugVisible(false)} style={{ borderRadius: radius.md }}>Close</Button>]}
        styles={{ content: { borderRadius: radius.xxxl } }}
      >
        {debugData && (
          <div>
            <Alert message="This data was sent to Docxtemplater" description="Compare these values with your Word template to find 'undefined' variables" type="info" showIcon style={{ marginBottom: '16px', borderRadius: radius.lg }} />
            <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
              {Object.entries(debugData).map(([key, value]) => {
                const displayValue = value === '' ? '(empty)' : value || '(undefined)';
                const isUndefined = !value && value !== '';
                return (
                  <div key={key} style={{ padding: '10px 12px', borderBottom: `1px solid ${colors.borderSubtle}`, backgroundColor: isUndefined ? colors.accentErrorBg : 'transparent' }}>
                    <Row>
                      <Col span={8}><Text strong code style={{ fontSize: '11px' }}>{`{{${key}}}`}</Text></Col>
                      <Col span={16}>
                        <Text style={{ color: isUndefined ? colors.accentError : 'inherit', fontSize: '11px' }}>{displayValue}</Text>
                        {isUndefined && <Tag color="orange" style={{ marginLeft: '8px', fontSize: '10px' }}>undefined</Tag>}
                      </Col>
                    </Row>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default CharterDocumentGenerator;
